import { useCallback, useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { tradingService } from "@/services/marketService";
import { errorMessage } from "@/services/apiClient";
import { formatSignedCurrency } from "@/lib/format";
import { LocalPager, useLocalPage } from "@/components/LocalPager";
import { PositionsTable } from "@/components/positions/PositionsTable";
import { PositionCards } from "@/components/positions/PositionCards";
import { useLanguage } from "@/context/LanguageContext";

const TABS = [
  { key: "open", labelKey: "positions.open" },
  { key: "pending", labelKey: "positions.pending" },
  { key: "closed", labelKey: "positions.closed" },
];

const TabButton = ({ tab, current, openCount, onSelect, tr }) => (
  <button
    onClick={() => onSelect(tab.key)}
    data-testid={`positions-tab-${tab.key}`}
    className={`flex items-center gap-2 rounded px-4 py-2 text-sm transition-colors ${
      current === tab.key ? "bg-[#132038] text-[#5B9CFF]" : "text-[#8A97AD] hover:text-white"
    }`}
  >
    {tr(tab.labelKey)}
    {tab.key === "open" ? (
      <span className="rounded bg-[#1D2637] px-2 py-0.5 font-mono text-[11px] text-white"
            data-testid="open-positions-count">
        {openCount}
      </span>
    ) : null}
  </button>
);

export const PositionsPanel = ({ refreshKey, onChanged }) => {
  const { t: tr } = useLanguage();
  const [tab, setTab] = useState("open");
  const [rows, setRows] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [openCount, setOpenCount] = useState(0);
  const { page, pages, setPage, slice, total } = useLocalPage(rows, 10);

  const load = useCallback(async () => {
    try {
      const data = await tradingService[tab]();
      setRows(data);
      if (tab === "open") setOpenCount(data.length);
      else setOpenCount((await tradingService.open()).length);
    } catch {
      setRows([]);
    }
  }, [tab]);

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load, refreshKey]);

  const close = useCallback(async (p) => {
    try {
      const r = await tradingService.close(p.id);
      toast.success(`${tr("trade.closed")} · P/L ${formatSignedCurrency(r.realized_pnl)}`);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, [load, onChanged, tr]);

  const hasRows = rows.length > 0;

  return (
    <section data-testid="positions-panel" className="rounded-md border border-[#141A26] bg-[#0A0E16]">
      <div className="flex items-center justify-between gap-4 p-4">
        <div className="flex flex-wrap gap-2 rounded-md border border-[#1D2637] p-1">
          {TABS.map((t) => (
            <TabButton key={t.key} tab={t} current={tab} openCount={openCount}
                       onSelect={setTab} tr={tr} />
          ))}
        </div>
        <button onClick={() => setCollapsed(!collapsed)} data-testid="toggle-positions-panel"
                className="rounded-md p-2 text-[#8A97AD] transition-colors hover:text-white">
          <ChevronDown className={`h-3 w-3 transition-transform ${collapsed ? "-rotate-90" : ""}`} />
        </button>
      </div>

      {collapsed ? null : (
        <div className="px-4 pb-4">
          {hasRows ? (
            <>
              <PositionsTable rows={slice} tab={tab} onClose={close} />
              <PositionCards rows={slice} tab={tab} onClose={close} />
              <LocalPager page={page} pages={pages} total={total} onPage={setPage} testId="positions-pager" />
            </>
          ) : (
            <div data-testid="positions-empty" className="rounded-md bg-[#111927] px-6 py-10">
              <p className="text-sm text-[#8A97AD]">{tr("positions.empty")}</p>
            </div>
          )}
        </div>
      )}
    </section>
  );
};
